import type { FlowActionKind } from './undoInspector/classifyFlowChange';

// -- Undo recording decision ----------------------------------------------------
// A continuous run of typing in ONE node is a single undo entry; anything else
// records its own and ends any open run.

export type RecordDecision =
  // Fold this text commit into the open typing run; do not push a new entry.
  | 'coalesce'
  // First commit of a typing run: push an entry and keep the session open for the
  // rest of the run to coalesce into it.
  | 'open-text-run'
  // Discrete edit: push an entry and end any open typing run.
  | 'record-discrete';

// Edit kinds that belong to a typing run, so they coalesce while a session is open.
// `message-edit` is normal typing; `edit` is the classifier's fallback for a text
// change it couldn't size (e.g. an equal-length replacement). A discrete edit
// (delete a bubble, change a persona) is never here.
const TYPING_RUN_KINDS: ReadonlySet<FlowActionKind> = new Set<FlowActionKind>([
  'message-edit',
  'edit',
]);

export function decideRecord(args: {
  actionKind: FlowActionKind;
  // Whether a per-node typing session is currently open (set by the node being
  // typed in; closed on pause, node change, discrete edit, or undo/redo).
  textSessionOpen: boolean;
  // Whether the open run has already pushed its undo entry.
  runAlreadyRecorded: boolean;
}): RecordDecision {
  const isTextRun = args.textSessionOpen && TYPING_RUN_KINDS.has(args.actionKind);
  if (!isTextRun) {
    return 'record-discrete';
  }
  return args.runAlreadyRecorded ? 'coalesce' : 'open-text-run';
}
